import { ClassValue, clsx } from 'clsx';
import { ReactNode } from 'react';
import { View } from '@shared/components/View';
import { Field } from '@shared/data/entity/fields/Field';
import { FieldVariant } from './FieldVariant';

/**
 * The value rendered as `data-field-type`, which per-view CSS modules select on.
 * Derived from the field union so it can't drift; `InputButton` is the one
 * static component that renders through this container without a field.
 */
export type CollectionViewFieldType = Field['fieldType'] | 'InputButton';

interface Props {
  children: ReactNode;
  fieldType: CollectionViewFieldType;
  /** Rendered as `data-variant`, beside `data-field-type`, for host CSS to style by. */
  variant: FieldVariant;
  className?: ClassValue;
  /** Text fields only: renders `data-multiline` so per-view CSS can wrap differently. */
  multiline?: boolean;
  /** Input button only: renders `data-with-label` for label spacing. */
  withLabel?: boolean;
}

/**
 * Every field variant's root: a styling hook, not a layout. Hosts style a field by its
 * `data-field-type` and `data-variant` from their own CSS modules, and own its alignment, size
 * and weight. An `inline` field is a span, so it flows in the host's line of text.
 */
export function CollectionViewFieldContainer({
  children,
  fieldType,
  variant,
  className,
  multiline,
  withLabel,
}: Props) {
  const dataProps = {
    'data-field-type': fieldType,
    'data-variant': variant,
    'data-multiline': multiline || undefined,
    'data-with-label': withLabel || undefined,
  };
  if (variant === 'inline') {
    return (
      <span className={clsx(className)} {...dataProps}>
        {children}
      </span>
    );
  }
  return (
    <View className={className} {...dataProps}>
      {children}
    </View>
  );
}
